import React, { useMemo, useState } from 'react';
import { Box, Typography, FormControlLabel, Switch, Autocomplete, TextField, Stack, Checkbox } from '@mui/material';
import dayjs from 'dayjs';
import { BarChart } from '@mui/x-charts/BarChart';

const COLORS = [
  '#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f',
  '#00796b', '#5d4037', '#455a64', '#7b1fa2', '#c2185b',
  '#0288d1', '#6a1b9a', '#388e3c', '#f57c00', '#c62828',
  '#009688', '#795548', '#607d8b', '#8e24aa', '#ad1457',
  '#ffa000', '#43a047', '#1e88e5', '#8d6e63', '#26c6da',
  '#f06292', '#7cb342', '#ff7043',
]

const UserLoginActivityChart = ({ data, title = 'User activity (logins)', height = 300 }) => {
  const [stacked, setStacked] = useState(true)
  const [selectedUsers, setSelectedUsers] = useState([])
  const { xAxis, series, allUsers, maxY, chartWidth } = useMemo(() => {
    const rows = Array.isArray(data) ? data.slice() : []
    // Sort dates: latest first (descending) so left is most recent
    rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    const dateLabels = rows.map(d => dayjs(d.date).format('MM-DD-YY'))
    const userSet = new Set()
    for (const row of rows) {
      Object.keys(row.users || {}).forEach(u => userSet.add(u))
    }
    const users = Array.from(userSet)
    const series = users.map((u, idx) => ({
      label: u,
      data: rows.map(d => (d.users?.[u] || 0)),
      color: COLORS[idx % COLORS.length]
    }))
    let maxY = 0
    for (const s of series) {
      for (const v of s.data) maxY = Math.max(maxY, v)
    }
    // narrower bars: reduce per-date width
    const chartWidth = Math.max(900, (dateLabels.length || 1) * 70)
    return {
      xAxis: [{ scaleType: 'band', data: dateLabels }],
      series,
      allUsers: users,
      maxY,
      chartWidth
    }
  }, [data])

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant='h6' sx={{ mb: 1 }}>{title}</Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <FormControlLabel control={<Switch checked={stacked} onChange={(e) => setStacked(e.target.checked)} />} label="Stacked" />
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          options={["__SELECT_ALL__", ...allUsers]}
          getOptionLabel={(opt) => (opt === "__SELECT_ALL__" ? "Select all" : opt)}
          value={selectedUsers}
          onChange={(_, val) => {
            if (val.includes("__SELECT_ALL__")) {
              if (selectedUsers.length === allUsers.length) {
                setSelectedUsers([]) // toggle off (unselect all)
              } else {
                setSelectedUsers(allUsers) // select all
              }
            } else {
              setSelectedUsers(val)
            }
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                style={{ marginRight: 8 }}
                checked={option === "__SELECT_ALL__" ? selectedUsers.length === allUsers.length && allUsers.length > 0 : selected}
              />
              {option === "__SELECT_ALL__" ? "Select all" : option}
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="Filter users" placeholder="Select users" />}
          sx={{ minWidth: 260 }}
        />
      </Stack>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ width: `${chartWidth}px` }}>
          {(() => {
            // Compute series after applying user filter and stack option for accurate Y scale
            const filteredSeries = (selectedUsers && selectedUsers.length > 0)
              ? series.filter(s => selectedUsers.includes(s.label))
              : series
            const plottedSeries = stacked
              ? filteredSeries.map(s => ({ ...s, stack: 'total' }))
              : filteredSeries

            // Calculate max Y considering stacking
            let computedMax = 0
            const points = xAxis[0]?.data?.length || 0
            if (stacked) {
              for (let i = 0; i < points; i++) {
                let sum = 0
                for (const s of filteredSeries) sum += Number(s.data[i] || 0)
                if (sum > computedMax) computedMax = sum
              }
            } else {
              for (const s of filteredSeries) {
                for (const v of s.data) if (Number(v) > computedMax) computedMax = Number(v)
              }
            }
            // Build integer ticks (avoid 1,1,2,2 duplicates)
            const ceilMax = Math.max(1, Math.ceil(computedMax))
            let step = 1
            if (ceilMax > 10) step = Math.ceil(ceilMax / 10)
            const ticks = []
            for (let t = step; t <= ceilMax; t += step) ticks.push(t)

            return (
          <BarChart
            xAxis={xAxis}
            yAxis={[{
              min: 0,
              max: ceilMax,
              tickValues: ticks,
              valueFormatter: (v) => `${v}`
            }]}
            series={plottedSeries}
            height={height}
            margin={{ left: 60, right: 20, top: 60, bottom: 40 }}
            slotProps={{ legend: { hidden: false, position: { vertical: 'top', horizontal: 'middle' } } }}
            width={chartWidth}
          />
            )
          })()}
        </Box>
      </Box>
    </Box>
  )
}

export default UserLoginActivityChart;



